import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { customerAPI, gtplSaathiAPI } from '../services/api';
import { format } from 'date-fns';
import { Search, Tv, GitCompareArrows } from 'lucide-react';
import ResponsiveTable from '../components/common/ResponsiveTable';
import useDarkReady from '../theme/useDarkReady';
import { HIDE_GTPL } from '../utils/demo';

// Read-only view over the STB fields the CRM already stores on each CATV customer
// (catv.stbModel / catv.setTopBoxSerial), plus each row's last known GTPL Saathi expiry
// date / box status (read from the last "GTPL vs CRM" check's saved results — see
// gtplCrmCheckService.getLatestByCustomers — not a live GTPL lookup). For a live, per-account
// GTPL check use the "Search on GTPL Saathi" panel on that customer's own page, and to
// correct CRM values FROM GTPL use the GTPL vs CRM checker's Box Model/Box Number fix.
export default function StbInventoryPage() {
  useDarkReady();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ search: '', stbModel: '', hasStb: '', page: 1, limit: 50 });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [stbModels, setStbModels] = useState([]);
  const [gtplData, setGtplData] = useState({}); // customerId -> { expiryDate, status, checkedAt }

  useEffect(() => {
    customerAPI.getStbModels().then(({ data }) => setStbModels(data.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    customerAPI.getAll({ ...filters, customerType: 'catv,both', sortBy: 'catv.setTopBoxSerial' })
      .then(({ data }) => { setRows(data.data || []); setPagination(data.pagination || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Cached GTPL data for exactly the rows on screen — one lightweight lookup per page load,
  // no live GTPL request (see the route's comment in routes/gtplSaathi.js).
  useEffect(() => {
    const ids = rows.map(r => r._id);
    if (HIDE_GTPL || !ids.length) { setGtplData({}); return; } // no GTPL lookup on the demo or an operator's copy
    gtplSaathiAPI.getCrmCheckByCustomers(ids).then(({ data }) => setGtplData(data.data || {})).catch(() => setGtplData({}));
  }, [rows]);

  const allColumns = [
    {
      key: 'name', label: 'Customer', title: true,
      render: c => <span style={{ fontWeight: 600 }}>{c.name}</span>,
    },
    { key: 'billingNumber', label: 'Billing #', render: c => c.billingNumber },
    { key: 'accountNumber', label: 'Account No.', render: c => c.catv?.accountNumber || '—' },
    {
      key: 'stbModel', label: 'STB Model', half: true,
      render: c => c.catv?.stbModel || <span style={{ color: 'var(--c-b91c1c)' }}>Not recorded</span>,
    },
    {
      key: 'stbSerial', label: 'STB Serial', half: true,
      render: c => c.catv?.setTopBoxSerial
        ? <span style={{ fontFamily: 'monospace' }}>{c.catv.setTopBoxSerial}</span>
        : <span style={{ color: 'var(--c-b91c1c)' }}>Not recorded</span>,
    },
    {
      // A box with a serial on file is "Issued" to this customer regardless of their account
      // status — the box itself is out in the field either way. Only when there's no box to
      // speak of does the account's own Active/Inactive status become the useful signal.
      key: 'status', label: 'Status', half: true,
      render: c => {
        if (c.catv?.setTopBoxSerial) {
          return <span style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--c-2563eb)' }}>Issued</span>;
        }
        const active = c.status === 'active';
        return (
          <span style={{ fontSize: '.78rem', fontWeight: 600, color: active ? 'var(--c-16a34a)' : 'var(--c-b91c1c)' }}>
            {active ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    {
      key: 'expiry', label: 'CRM Expiry', half: true,
      render: c => c.catv?.expiryDate ? format(new Date(c.catv.expiryDate), 'dd MMM yyyy') : '—',
    },
    {
      // From the last GTPL vs CRM check's saved results, not a live lookup — see the
      // useEffect above. Blank until a check has been run with "Expiry Date" ticked.
      key: 'gtplExpiry', label: 'GTPL Expiry', half: true,
      render: c => {
        const g = gtplData[c._id];
        if (!g) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
        if (!g.expiryDate) return <span style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>Not checked</span>;
        return <span>{g.expiryDate}</span>;
      },
    },
    {
      key: 'gtplStatus', label: 'GTPL Status', half: true,
      render: c => {
        const g = gtplData[c._id];
        if (!g?.status) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
        return <span style={{ fontSize: '.78rem' }}>{g.status}</span>;
      },
    },
  ];
  // GTPL is Laxmi-only: its two columns and the verify button never show on the demo or an operator's copy.
  const columns = HIDE_GTPL ? allColumns.filter(c => !c.key.startsWith('gtpl')) : allColumns;

  return (
    <div className="page-content">
      <div className="customers-header mb-4">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tv size={18} /> STB Inventory
          </h2>
          <p className="text-muted text-small">{pagination.total || 0} set-top boxes on file</p>
        </div>
        {!HIDE_GTPL && (
          <Link to="/gtpl-vs-crm" className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <GitCompareArrows size={14} /> Verify vs GTPL Saathi
          </Link>
        )}
      </div>

      <div className="filter-bar customers-filters">
        <div className="search-input-wrapper customers-search">
          <Search size={14} className="search-icon" />
          <input
            className="search-input" placeholder="Search customer, account no., serial..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
          />
        </div>
        <select className="form-control customers-select" value={filters.stbModel}
          onChange={e => setFilters(f => ({ ...f, stbModel: e.target.value, page: 1 }))}>
          <option value="">All Models</option>
          {stbModels.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="form-control customers-select" value={filters.hasStb}
          onChange={e => setFilters(f => ({ ...f, hasStb: e.target.value, page: 1 }))}>
          <option value="">Has / No Serial</option>
          <option value="yes">Has Serial</option>
          <option value="no">Missing Serial</option>
        </select>
        <select className="form-control customers-select-sm" value={filters.limit}
          onChange={e => setFilters(f => ({ ...f, limit: Number(e.target.value), page: 1 }))}>
          <option value={25}>25/pg</option>
          <option value={50}>50/pg</option>
          <option value={100}>100/pg</option>
        </select>
        <button className="btn btn-secondary btn-sm"
          onClick={() => setFilters({ search: '', stbModel: '', hasStb: '', page: 1, limit: 50 })}>
          Reset
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <Tv size={32} />
              <h3>No STBs found</h3>
              <p>Try changing your filters.</p>
            </div>
          ) : (
            <ResponsiveTable
              columns={columns}
              rows={rows}
              rowKey={c => c._id}
              onRowClick={c => navigate(`/customers/${c._id}`)}
            />
          )}
        </div>

        {pagination.pages > 1 && (
          <div className="pagination">
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginRight: 8 }}>
              Page {pagination.page} of {pagination.pages} · {pagination.total} total
            </span>
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>
    </div>
  );
}
